package com.iregained.site.components.sections.financing.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.corporate.styles.ActionButtonStyle
import com.iregained.site.components.sections.financing.styles.ClickableLogoStyle
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.region.Region
import com.iregained.site.region.RegionConfig
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.navigation.OpenLinkStrategy
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATION: Vault Reveal
@Composable
private fun Modifier.vaultReveal(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateY(if (isVisible) 0.px else 30.px)
        .scale(if (isVisible) 1f else 0.98f).transition(
            Transition.of("opacity", 0.7.s, TransitionTimingFunction.EaseOut, delayMs.ms),
            Transition.of("transform", 0.7.s, TransitionTimingFunction.EaseOut, delayMs.ms)
        )
}

@Composable
fun FinancingPartnersSection(data: FinancingSectionData = defaultFinancingData) {
    val palette = ColorMode.current.toSitePalette()
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    var isVisible by remember { mutableStateOf(false) }

    if (data.partners.isNotEmpty()) {
        Box(
            modifier = Modifier.id(data.sectionId ?: "financing-partners-section")
                .styleModifier { property("scroll-margin-top", "100px") }.fillMaxWidth()
                .background(palette.custom.lightGray)
                .padding(topBottom = if (isDesktop) 80.px else 40.px, leftRight = 5.percent),
            contentAlignment = Alignment.Center
        ) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex)
                    .justifyContent(org.jetbrains.compose.web.css.JustifyContent.Center).toAttrs {
                        ref { element ->
                            val observer = IntersectionObserver { entries, obs ->
                                entries.forEach {
                                    if (it.isIntersecting) {
                                        isVisible = true; obs.unobserve(element)
                                    }
                                }
                            }
                            observer.observe(element); onDispose { observer.disconnect() }
                        }
                    }) {
                Column(
                    modifier = Modifier.fillMaxWidth().maxWidth(1200.px),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    SectionHeader(
                        text = data.headline,
                        fontSize = 36.px,
                        lineWidthPercent = 25.percent,
                        modifier = Modifier.margin(bottom = 16.px).vaultReveal(isVisible, 0),
                        alignment = Alignment.CenterHorizontally
                    )

                    Div(
                        attrs = Modifier.maxWidth(800.px).fontSize(18.px).fontWeight(FontWeight.Medium)
                            .color(palette.custom.darkText).textAlign(TextAlign.Center).margin(bottom = 60.px)
                            .vaultReveal(isVisible, 150)
                            .toAttrs { ref { el -> el.innerHTML = data.subtitle; onDispose {} } })

                    Column(modifier = Modifier.fillMaxWidth().gap(40.px)) {
                        data.partners.forEachIndexed { index, partner ->
                            key(partner.partnerName) {
                                // Cascading delay for the cards: 300ms, 450ms, 600ms...
                                Box(modifier = Modifier.fillMaxWidth().vaultReveal(isVisible, 300 + (index * 150))) {
                                    FinancingPartnerCard(partner, palette)
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun FinancingPartnerCard(partner: FinancingPartnerData, palette: com.iregained.site.theme.SitePalette) {
    SimpleGrid(
        numColumns = numColumns(base = 1, lg = 2),
        modifier = Modifier.fillMaxWidth().backgroundColor(Colors.White).borderRadius(12.px)
            .border(1.px, LineStyle.Solid, palette.custom.divider).padding(40.px).gap(40.px)
    ) {
        // Image Container
        Box(modifier = Modifier.fillMaxSize().minHeight(200.px), contentAlignment = Alignment.Center) {
            if (partner.logoUrl != null) {
                Link(
                    path = partner.logoUrl,
                    // Tells Kobweb to open in a new tab if it's an external link
                    openExternalLinksStrategy = OpenLinkStrategy.IN_NEW_TAB
                ) {
                    Image(
                        src = partner.partnerLogo,
                        alt = partner.partnerName,
                        modifier = ClickableLogoStyle.toModifier().maxWidth(250.px)
                    )
                }
            }
        }

        // Content Container
        Column(verticalArrangement = Arrangement.Center) {
            SpanText(
                text = partner.partnerName,
                modifier = Modifier.fontSize(28.px).fontWeight(FontWeight.Bold).color(palette.brand.primary)
                    .margin(bottom = 16.px)
            )
            SpanText(
                text = partner.description,
                modifier = Modifier.fontSize(16.px).lineHeight(1.6).color(palette.custom.darkText)
                    .margin(bottom = 24.px)
            )

            Column(modifier = Modifier.gap(12.px).margin(bottom = 32.px)) {
                partner.benefits.forEach { benefit ->
                    key(benefit) {
                        Row(
                            verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(10.px)
                        ) {
                            SpanText(
                                "✓", modifier = Modifier.color(palette.brand.primary).fontWeight(FontWeight.Bold)
                            )
                            SpanText(
                                benefit, modifier = Modifier.fontSize(15.px).color(palette.custom.darkText)
                            )
                        }
                    }
                }
            }

            Link(
                path = partner.applyUrl, modifier = Modifier.textDecorationLine(TextDecorationLine.None)
            ) {
                Box(
                    modifier = ActionButtonStyle.toModifier().borderRadius(8.px).backgroundColor(palette.brand.primary)
                        .padding(topBottom = 12.px, leftRight = 24.px).cursor(Cursor.Pointer),
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        "Apply for Financing",
                        modifier = Modifier.color(Colors.White).fontSize(16.px).fontWeight(FontWeight.Bold)
                    )
                }
            }
        }
    }
}

// --- CMS DATA MODEL ---
data class FinancingPartnerData(
    val partnerLogo: String,
    val logoUrl: String? = null,
    val partnerName: String,
    val description: String,
    val benefits: List<String>,
    val applyUrl: String
)

data class FinancingSectionData(
    val sectionId: String? = "financing-partners-section",
    val headline: String,
    val subtitle: String,
    val partners: List<FinancingPartnerData>
)

// --- REGION SWITCHBOARD ---
private val usFinancingData = FinancingSectionData(
    headline = "US Financing Options",
    subtitle = "We have partnered with leading healthcare lenders to help you procure the MyHand® System with manageable monthly payments.",
    partners = listOf(
        FinancingPartnerData(
            partnerLogo = Res.Images.Financing.US_AMERICORP,
            logoUrl = "https://americorpusa.com/",
            partnerName = "Americorp Financial LLC",
            description = "Americorp Financial LLC provides healthcare sector nationwide with flexible, cost-effective financing programs designed to fulfill the equipment needs for your hospital, clinic, and private practice.",
            benefits = listOf(
                "Tax Benefits: IRS Section 179",
                "Flexible Financing Programs",
                "Easy One Page Credit Application",
                "Competitive Fixed Rates",
                "Avoid Technological Obsolescence"
            ),
            applyUrl = "/contact"
        )
    )
)

private val canadaFinancingData = FinancingSectionData(
    headline = "Canadian Financing Programs",
    subtitle = "Secure the clinical rehabilitation technology you need today while preserving capital.",
    partners = listOf(
        FinancingPartnerData(
            partnerLogo = Res.Images.Financing.CA_VENDOR_LENDER,
            logoUrl = "https://share.google/MPFcKfDQcVCrR9frV",
            partnerName = "Vendor Lender",
            description = "Vendor Lender is a company specializing in equipment financing and leasing, located at Markham, ON. They offer financial products including loans and rentals for medical industries.",
            benefits = listOf("Rapid Approvals", "Streamlined Documentation", "Digital Portal Access"),
            applyUrl = "/contact"
        )
    )
)

private val indiaFinancingData = FinancingSectionData(
    headline = "India EMI & Financing Partners",
    subtitle = "Convert the capital acquisition cost into low-cost EMIs through financing or lease",
    partners = listOf(
        FinancingPartnerData(
            partnerLogo = Res.Images.Financing.IN_POONAWALLA_FINCORP,
            logoUrl = "https://poonawallafincorp.com/",
            partnerName = "Poonawalla FinCorp",
            description = "Access the MyHand® System immediately using India's leading health EMI network.",
            benefits = listOf(
                "Collateral-Free Capital",
                "100% Paperless Processing",
                "Zero Hidden Charges",
                "Instant Approval Mechanics",
                "Flexible Tenure Frameworks"
            ),
            applyUrl = "/contact"
        )
    )
)

val defaultFinancingData: FinancingSectionData
    get() = when (RegionConfig.currentRegion) {
        Region.US -> usFinancingData
        Region.INDIA -> indiaFinancingData
        Region.CANADA -> canadaFinancingData
    }